import "@/styles/Layout.scss";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import apiClient from "@/services";
import { getUser } from "@/hooks/useLocalStorage";
import getConstants from "@/scripts/getConstants";
import convertTimestamp from "@/scripts/convertTimestamp";

import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ListBulletIcon, DotsHorizontalIcon } from "@radix-ui/react-icons";
import Spinner from "@/components/Spinner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import Peak from "@/components/Peak";

function ManageProducts() {
  const user = getUser();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  // Constants
  const [forms, setForms] = useState([]);
  const [grades, setGrades] = useState([]);

  // Filters
  const [showFilter, setShowFilter] = useState(true);
  const defaultFilters = {
    startDate: "",
    endDate: "",
    grade: "all",
    form: "all",
    status: "under review",
    company: "", // New company filter
  };
  const [filters, setFilters] = useState(() => {
    const saved = localStorage.getItem("manage_product_filters");
    return saved ? JSON.parse(saved) : defaultFilters;
  });
  const [filteredList, setFilteredList] = useState([]);

  // Define columns for the table
  const columns = [
    {
      accessorKey: "reference_id",
      header: "Reference ID",
    },
    {
      accessorKey: "company",
      header: "Company",
    },
    {
      accessorKey: "name",
      header: "Person",
    },
    {
      accessorKey: "created_at",
      header: "Date",
      cell: (info) => convertTimestamp(info.getValue()), // Custom rendering for date
    },
    {
      accessorKey: "product.grade",
      header: "Grade",
    },
    {
      accessorKey: "product.form",
      header: "Form",
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: (info) => (
        <div className={`status ${info.getValue()}`}>
          {capitalizedStatus(info.getValue())}
        </div>
      ), // Custom rendering for status
    },
    {
      accessorKey: "actions",
      header: "Actions",
      cell: (info) => (
        <div className="flex gap-6">
          {/* View Product Button */}
          <Button
            className="px-6 rounded-full"
            onClick={() => viewProduct(info.row.original)}
          >
            View
          </Button>

          <Peak 
            refId={info.row.original.reference_id}
            allImages={info.row.original.product.images} 
          />

          {/* Action Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger className="h-9 px-6 py-2 rounded-full bg-black text-white">
              <DotsHorizontalIcon className="h-6 w-6" />
            </DropdownMenuTrigger>
            <DropdownMenuContent className="flex flex-col w-[200px] p-4 gap-3">
              {info.row.original.status !== "approved" && (
                <Button
                  className="bg-green"
                  onClick={() =>
                    handleClick("approve", info.row.original.request_id)
                  }
                >
                  Approve
                </Button>
              )}
              {info.row.original.status !== "rejected" && (
                <Button
                  className="bg-[#e03939]"
                  onClick={() =>
                    handleClick("reject", info.row.original.request_id)
                  }
                >
                  Reject
                </Button>
              )}
              {info.row.original.status !== "escalated" && (
                <Button
                  className="bg-[#ff9720]"
                  onClick={() =>
                    handleClick("escalate", info.row.original.request_id)
                  }
                >
                  Escalate
                </Button>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ), // Custom rendering for actions
    },
  ];

  // Initialize the table
  const table = useReactTable({
    columns,
    data: filteredList,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 10, // Default page size
      },
    },
  });

  const viewProduct = (item) => {
    navigate("/product", { state: { data: item } });
  };

  // Capitalize first letter
  const capitalizedStatus = (status) => {
    if (status === "processing") {
      return "Under Review";
    }
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const handleFilterChange = (name, value) => {
    setFilters({ ...filters, [name]: value });
  };

  // Filter product logic
  const filterProducts = (listToFilter = list) => {
    const filtered = listToFilter.filter((product) => {
      // Convert Firestore Timestamp to JavaScript Date
      const productDate = new Date(product.created_at);

      // Parse startDate and endDate
      const startDate = filters.startDate ? new Date(filters.startDate) : null;
      const endDate = filters.endDate ? new Date(filters.endDate) : null;

      const matchDate =
        (!startDate || productDate >= startDate) &&
        (!endDate ||
          productDate <= new Date(new Date(endDate).setHours(23, 59, 59, 999)));

      const matchGrade =
        filters.grade && filters.grade !== "all"
          ? product.product.grade === filters.grade
          : true;

      const matchForm =
        filters.form && filters.form !== "all"
          ? product.product.form === filters.form
          : true;

      const matchStatus =
        filters.status === "all"
          ? true
          : filters.status === "under review"
          ? product.status === "processing"
          : product.status === filters.status;

      // Check if the product's company matches the filter (case-insensitive)
      const matchCompany =
        !filters.company ||
        (product.company &&
          String(product.company)
            .toLowerCase()
            .includes(String(filters.company).toLowerCase()));

      return (
        matchDate && matchGrade && matchForm && matchStatus && matchCompany
      );
    });

    setFilteredList(filtered); // Update the filteredList state
  };

  // Get all requests
  const getData = async () => {
    try {
      setLoading(true);
      const requestData = await apiClient.post("/getRequests", {
        role: user.role,
      });

      // Update the main list and apply the current filter to update the filtered list
      const fetchedList = requestData.data;
      setList(fetchedList);

      // Apply filters after setting the list
      filterProducts(fetchedList);
    } catch (error) {
      console.log("Error fetching data", error);
    } finally {
      setLoading(false);
    }
  };

  // Action on request
  const handleClick = async (action, requestId) => {
    try {
      setLoading(true);

      const endpoint =
        user.role === "buyer_agent"
          ? "manageBuyerRequest"
          : "manageSellerRequest";

      const { status } = await apiClient.post(`/${endpoint}`, {
        action,
        requestId,
      });

      if (status === 200) {
        // Fetch updated data after successful action
        await getData();
      }
    } catch (error) {
      console.error("Error updating data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Constants - Forms, Grades
  const fetchConstants = async () => {
    try {
      const { forms, grades } = await getConstants();
      // Set values
      setForms(forms);
      setGrades(grades);
    } catch (err) {
      console.log("Unable to fetch constants", err);
    }
  };

  useEffect(() => {
    getData(); // Fetch data once on page load
    fetchConstants();
  }, []);

  useEffect(() => {
    filterProducts();
    localStorage.setItem("manage_product_filters", JSON.stringify(filters));
  }, [filters]);

  if (loading || !grades || !forms) {
    return <Spinner />;
  }

  return (
    <div className="sectionWrapper mobileWrapper mobileTableWrapper">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-sm font-bold text-green ml-0.5">
            {showFilter ? "Product Filter" : "Product Form"}
          </h1>
          <div className="highlight mb-3" />
        </div>

        <Button onClick={() => setShowFilter(!showFilter)}>
          <ListBulletIcon />
          Filters
        </Button>
      </div>

      {
        /* Filters */
        showFilter && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium mb-1">Status</label>
                <Select
                  onValueChange={(value) => handleFilterChange("status", value)}
                  value={filters.status}
                >
                  <SelectTrigger className="w-full">
                    <span>
                      {capitalizedStatus(filters.status) || "Select Status"}
                    </span>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="escalated">Escalated</SelectItem>
                    <SelectItem value="under review">Under Review</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Grade Filter */}
              <div>
                <label className="block text-sm font-medium mb-1">Grade</label>
                <Select
                  onValueChange={(value) => handleFilterChange("grade", value)}
                  value={filters.grade}
                >
                  <SelectTrigger className="w-full">
                    <span>
                      {capitalizedStatus(filters.grade) || "Select Grade"}
                    </span>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Grades</SelectItem>
                    {/* Map */}
                    {grades.map((grade, i) => (
                      <SelectItem key={i} value={grade}>
                        {grade}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Form Filter */}
              <div>
                <label className="block text-sm font-medium mb-1">Form</label>
                <Select
                  onValueChange={(value) => handleFilterChange("form", value)}
                  value={filters.form}
                >
                  <SelectTrigger className="w-full">
                    <span>
                      {capitalizedStatus(filters.form) || "Select Form"}
                    </span>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Forms</SelectItem>
                    {/* Map */}
                    {forms.map((form, i) => (
                      <SelectItem key={i} value={form}>
                        {form}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Company Filter */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Company
                </label>
                <Input
                  type="text"
                  placeholder="Search by company"
                  value={filters.company}
                  onChange={(e) =>
                    handleFilterChange("company", e.target.value)
                  }
                />
              </div>

              {/* Start Date */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Start Date
                </label>
                <Input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) =>
                    handleFilterChange("startDate", e.target.value)
                  }
                />
              </div>

              {/* End Date */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  End Date
                </label>
                <Input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) =>
                    handleFilterChange("endDate", e.target.value)
                  }
                />
              </div>
            </div>

            <div>
              <h1 className="text-sm font-bold text-green ml-0.5">
                Product Form
              </h1>
              <div className="highlight highlight-md mb-3" />
            </div>
          </>
        )
      }

      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Pagination Controls */}
      <div className="flex items-center justify-between mt-4">
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Previous
          </Button>
          <Button
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
          </Button>
        </div>

        <div className="flex items-center gap-4">
          <span className="text-sm">
            Page {table.getState().pagination.pageIndex + 1} of{" "}
            {table.getPageCount()}
          </span>
          <Select
            value={table.getState().pagination.pageSize.toString()}
            onValueChange={(value) => table.setPageSize(Number(value))}
          >
            <SelectTrigger className="w-[80px]">
              <span className="text-sm">
                {table.getState().pagination.pageSize}
              </span>
            </SelectTrigger>
            <SelectContent className="!w-[80px]">
              {[10, 25, 50].map((size) => (
                <SelectItem key={size} value={size.toString()}>
                  Show {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

export default ManageProducts;
